// The map-object cards (ui-map-interface.md §8 "The card family"; mockup
// round 084 cards 04 to 09).
//
// One function per kind of map object turns the client's state into a
// `CardSpec`: an own fleet, a fleet of the reader's faction, a sonar
// contact, a hold, a strategic object, a deposit, and the probe scan
// report as a list card. The selection draws the spec as a card, and the
// map hover draws the same spec as a peek, so the two say a thing in the
// same words (R1810).
//
// A card prints only what the frame carries (R198). A value the reader
// may not see is the hatched "?" of the vital, never a missing vital, so
// a card keeps its height from one hull to the next.
//
// The module holds no state. `MapCardCtx` is what it reads of the
// overlay: the game state, the squad test, the ⚑ REPORT marks, the armed
// picks and the open ⋯.

import {
  AnchorState, Building, HitAspect, Mission, NpcAct, NpcKind, ShipClass, SignatureCharge, SignatureOrder, SignatureTrigger, SiteKind,
} from '../../gen/proto/aquaphobia';
import type { ChatMsg, PledgedFleet, PointOfInterest, ShipSpec, Site, Wreck } from '../../gen/proto/aquaphobia';
import { CacheKind, PoiKind, PoiMonster } from '../../gen/proto/aquaphobia';
import { poiRules } from '../../render/poi-rules';
import { bandAt } from '../../deep';
import { bandWord } from '../chart/deep-legend';
import { KEYS, SCAN } from '../../gen/presentation';
import { card as S, markers as MS, overlay as OS } from '../../gen/strings';
import { GROUP_SLOTS } from '../../groups';
import { pad2, isoTime } from '../../locale/stamp';
import { type GameState, MISSION_NAMES, dur, num } from '../../state';
import { cellAt, factionName, mpxToPx } from '../../world';
import { esc } from '../esc';
import { type Choice, pillGroup } from '../choice/choice';
import { PLAN_BEH_SHIFT, PLAN_BEHAVIOURS, PLAN_POS_MASK, PLAN_POSITIONS } from '../overlay/tables';
import {
  anchorLabel, benefitShort, benefitText, cssColor, holdTotal, hullBand, pctFig, pxFig, ratioFig, speedPxMin, typeText,
} from '../format';
import { fitStrip, liveNoise, missionLabel, regenWaitSec, sitePlace } from '../overlay/markup';
import {
  hullMagazine, kindsAboard, loadableKinds, mixText, pickKinds, roundOrder, roundPickHtml,
} from '../rounds';
import type { UIFleet } from '../overlay/types';
import {
  armState, gaugeVital, groupChipHtml, guardChip, heldByFact, tubeLoading, type Action, type ArmLine, type CardSize, type CardSpec, type Fact, type GuardChip,
  type GuardRead, type MoreSection, type Vital,
} from './card';
import { markSvg, type MarkKind } from './marks';
import { MONSTER_CLASS_WORDS, monsterDesignWord, monsterKindOf } from '../../render/monster-marks';
import { cachesItem, compressedFact, echoFact, pointSearch, workFact, wreckCargoFact, wreckTakes } from './treasure';
import { BROTHERHOOD_WORDS } from './dens';
import { harbourPlanHtml } from './harbour';
import { featuredTagHtml } from '../siegewindow';
import { condsOf } from './condsmap';
import { coursePoints, depthFact, routeProfile, routeProfileHtml } from './routeprofile';
import { scanLook } from './scanage';
import { fleetOrderGate, orderRow, type OrderId } from './orders';
import { collectAction, isSapperCard, layAction, layPickHtml, sapperFacts, stowSectionHtml, stowVital } from './sapper';
import {
  aspectOf, aspectRef, aspectWord, chargeText, floorLeftSec, signatureAction, signatureRecharge, signatureRowHtml, signatureSection, signatureState,
} from './approach';
import { beamAction, beamRowHtml, beamState } from './beam';
import { laneOfferRowHtml } from './laneoffer';

/** A fleet as the overlay answers `contact`: the UI fleet with whose it is. */
export type CardFleet = UIFleet & { own: boolean; faction: number; classified: boolean };

/** What a map card reads of the overlay. */
export interface MapCardCtx {
  readonly s: GameState;
  /** The own fleets on the map, for SEND HAULER and COMMIT. */
  fleets(): readonly UIFleet[];
  squadmate(accountId: number): boolean;
  /** The ⚑ REPORT mark after a commander's name, or ''. */
  commanderMark(accountId: number, name: string): string;
  /** The ⚑ REPORT mark after the name of a friendly hull, or ''. */
  hullMark(f: CardFleet): string;
  /** The order of this act waits for a pick on the map for this fleet. */
  picking(act: string, fleetId: number): boolean;
  /** The armed aim: an own hull, 0 for the hold's own battery, or null. */
  aiming: number | null;
  /** The armed order of an own hull, or of the hold's battery for 0
   *  (R2164): the order word and the line of the next map press, or null
   *  while nothing is armed for it. */
  armed(id: number): { order: string; line: string; aim: boolean } | null;
  /** The base round the hold card fires. */
  baseTorp: number;
  /** The ⋯ of this fleet's card is open. */
  moreOpen(fleetId: number): boolean;
  /** The EDIT grid of this fleet's card is open (R2008). */
  editOpen(fleetId: number): boolean;
  /** The control group slot a fleet holds, or 0. */
  groupOf(fleetId: number): number;
  /** The seat may rename a hull (the `fleet_designer` feature). */
  canRename: boolean;
  /** Map seconds a scan reveal still holds a contact, or null (R1837). */
  scanLeftSec(fleetId: number): number | null;
  /** The hull draws its chevrons at full alpha: a hostile is in reach
   *  (`FleetLayer.chevronsShown`). The aspect fact reads it (R2457). */
  chevronsShown(fleetId: number): boolean;
  /** The chart reading is historical: the harbour plan section of the
   *  hold card takes the Fell steps (R1824). */
  historical: boolean;
  /** The page places map markers: the contact card carries MARK (R2002). */
  canMark: boolean;
  /** The clock of an own hull that is a guard (R2080), or null: the share
   *  left, the map seconds left, and what the map draws now. */
  guard(fleetId: number): GuardRead | null;
  /** The kind the client laid last with this Sapper, or 0 (R2635): LAY
   *  of the order row lays it again. A page with no Sapper leaves it out. */
  lastLay?(fleetId: number): number;
  /** The heading and the telegraph step that the helm keys of the map
   *  order a fleet, or null (R2707). A page with no helm leaves it out. */
  helm?(fleetId: number): { headingDeg: number; step: number; top: number } | null;
  /** The design and the act of a monster the monster layer draws, or null
   *  (R2719). A page with no monster layer leaves it out. */
  monster?(fleetId: number): { kind: number; design: number; act: NpcAct } | null;
  /** The on-ramp offer that stands for an own fleet, or null (R3075): the
   *  name of the lane and the map seconds it saves. A page with no offer
   *  leaves it out. */
  laneOffer?(fleetId: number): { lane: string; saveMapSec: number } | null;
}


/** The key cap of a map key, in capitals. */
function cap(id: keyof typeof KEYS.map): string {
  return KEYS.map[id].label.toUpperCase();
}

/** The figure of a quantity whose maximum the catalog may not know. */
function capped(pct: number, max: number | undefined): string {
  return max ? ratioFig(Math.round(pct * max / 100), max) : pctFig(pct);
}

/** HULL and SHIELD of a fleet whose condition the frame carries. `regen`
 *  is the seconds of the shield regen wait, 0 for none (R1853); `fade` is
 *  the fill of a scan reveal that runs out (R1837); 1 draws no fade. */
function hullShield(f: { hullPct: number; shieldPct: number }, hullMax?: number, shieldMax?: number,
  regen = 0, fade = 1): Vital[] {
  const fading = fade < 1 ? { fade } : {};
  return [
    { label: S.v_hull, fig: capped(f.hullPct, hullMax), frac: f.hullPct / 100, low: hullBand(f.hullPct) === 'bad', ...fading },
    { label: S.v_shield, fig: capped(f.shieldPct, shieldMax), frac: f.shieldPct / 100, ink: 'shield', wait: regen, ...fading },
  ];
}

/** HULL and SHIELD that the reader may not see: two hatched bars. */
function unknownHullShield(): Vital[] {
  return [
    { label: S.v_hull, fig: '', frac: 0, unknown: true },
    { label: S.v_shield, fig: '', frac: 0, unknown: true, ink: 'shield' },
  ];
}

/**
 * The words of the telegraph steps 0 to `MAP_HELM.topStep`, the words of
 * the engine telegraph of the helm view (R2718).
 */
export const TELEGRAPH_WORDS: readonly string[] = [S.telegraph_stop, S.telegraph_slow, S.telegraph_half, S.telegraph_full];

/** The word of telegraph step `step`; a step past the list reads as its last word. */
export function telegraphWord(step: number): string {
  return TELEGRAPH_WORDS[Math.max(0, Math.min(TELEGRAPH_WORDS.length - 1, Math.round(step)))];
}

/** The order chip of a hull: the anchor cycle while it runs, else the mission. */
function orderChip(s: GameState, f: UIFleet): string {
  if (f.anchorLeftSec > 0 && f.anchor !== AnchorState.ANCHOR_STATE_UNSPECIFIED) return anchorLabel(f, s);
  return missionLabel(f);
}

// --- the own fleet (084 card 04) -----------------------------------------

/**
 * One press of the order row (R1809). The label, the gate of a running
 * countdown, the lit state of a pick that waits and the key cap are the
 * ones the old mission row carried; `face` is the place on the row.
 */
export function orderAction(ctx: MapCardCtx, f: UIFleet, o: OrderId, face: 'dark' | 'quiet'): Action {
  const s = ctx.s;
  // A cable out pins the hull (R114): the server refuses a movement order.
  const pinned = f.anchor !== AnchorState.ANCHOR_STATE_UNSPECIFIED;
  const pick = (act: string, idle: string, waiting: string, key: keyof typeof KEYS.map): Action => {
    const on = ctx.picking(act, f.id);
    return { act, id: f.id, face, label: on ? waiting : idle, on, off: pinned, key: cap(key) };
  };
  const wait = (sec: number) => dur(s.mapToWorldSec(sec));
  switch (o) {
    case 'fire': return {
      act: 'aim', id: f.id, face, key: cap('fire'),
      label: ctx.aiming === f.id ? S.o_aiming : S.o_fire, on: ctx.aiming === f.id,
      off: f.reloadSec > 0 || f.torpedoes === 0,
    };
    case 'scan': return {
      act: 'scan', id: f.id, face, key: cap('scan'), off: f.scanReadySec > 0,
      label: f.scanReadySec > 0 ? S.o_scan_wait({ left: wait(f.scanReadySec) }) : S.o_scan,
    };
    case 'gather': return pick('gatherpick', S.o_gather, S.o_pick_field, 'gather');
    // SEARCH of a fit hull (v2 §9.6, R2834): it arms a pick of a cell of
    // the map. No key carries it.
    case 'search': {
      const on = ctx.picking('searchpick', f.id);
      return { act: 'searchpick', id: f.id, face, label: on ? S.o_pick_cell : S.o_search, on, off: pinned };
    }
    case 'share': return {
      act: 'share', id: f.id, face, key: cap('scan'), off: f.shareSec > 0,
      label: f.shareSec > 0 ? S.o_share_left({ left: wait(f.shareSec) }) : S.o_share,
    };
    case 'anchor': return {
      act: 'anchor', id: f.id, face, key: cap('anchor'), label: anchorLabel(f, s),
      on: f.anchor === AnchorState.ANCHOR_STATE_SET,
    };
    case 'deep_run': return {
      act: 'deeprun', id: f.id, face, on: f.deepRun, label: f.deepRun ? S.o_surface : S.o_deep_run,
    };
    case 'patrol': return pick('patrol', S.o_patrol, S.o_pick_lane, 'patrol');
    // PLOT opens the plot line of a course (v2 §7, R2589). It takes the key
    // of PATROL, which it replaces under the flag `routes`.
    case 'plot': return { act: 'plot', id: f.id, face, label: S.o_plot, off: pinned, key: cap('patrol') };
    case 'hunt': return pick('hunt', S.o_hunt, S.o_pick_point, 'hunt');
    case 'move': return pick('move', S.o_move, S.o_pick_point, 'move');
    case 'escort': return pick('escort', S.o_escort, S.o_pick_target, 'escort');
    case 'defend': return {
      act: 'mission', id: f.id, n: Mission.MISSION_DEFEND, face, label: S.o_hold, off: pinned, key: cap('defend'),
    };
    case 'return': return { act: 'return', id: f.id, face, label: S.o_return, off: pinned, key: cap('return') };
    case 'hold_fire': return {
      act: 'holdfire', id: f.id, face, on: f.holdFire, label: S.o_hold_fire, key: cap('hold_fire'),
    };
    case 'decoy': return {
      act: 'decoy', id: f.id, face, key: cap('fire'), off: f.decoyReadySec > 0,
      label: f.decoyReadySec > 0 ? S.o_decoy_wait({ left: wait(f.decoyReadySec) }) : S.o_decoy,
    };
    case 'auto_field': return {
      act: 'autofield', id: f.id, face, on: !f.autoFieldOff, label: S.o_auto_field,
    };
    // LAY lays the last kind again and names it (round 088 card 12 b,
    // R2635); COLLECT stands in ⋯ (R2651). LAY takes the key of FIRE and
    // COLLECT the key of SCAN: a Sapper has no tubes and no scan (R2710).
    case 'lay': return layAction(s, f, ctx.lastLay?.(f.id) ?? 0, face, cap('fire'));
    case 'collect': return { ...collectAction(f, face, cap('scan')), off: pinned };
  }
}

/** The fourth vital: the one fraction THIS hull keeps, or none. */
function loadVital(s: GameState, f: UIFleet, spec: ShipSpec | undefined): Vital | null {
  // The Sapper fills its cargo with the stow (v2 §11.1, R2654).
  if (isSapperCard(s, f.cls)) return stowVital(s, f, spec);
  const armed = (spec?.torpedoes.length ?? 0) > 0;
  // A stowing hull takes rounds aboard it can never fire (R712).
  if (armed || (spec?.stows.length ?? 0) > 0) {
    const magazine = hullMagazine(spec, f.firingKind, (k) => s.torpedo(k));
    const reloading = armed && f.reloadSec > 0;
    const fig = magazine ? ratioFig(f.torpedoes, magazine) : num(f.torpedoes);
    // A mixed load states its mix in the short form, `6H · 3R`, and the
    // hover states it in full with the ratio (R2031).
    const load = f.rounds ?? [];
    const mixed = kindsAboard(load) > 1;
    const nameOf = (k: number) => s.torpedo(k)?.name ?? '?';
    return {
      label: armed ? S.v_tubes : S.v_rounds, ink: 'load',
      fig: mixed ? esc(mixText(load, nameOf, true)) : f.torpedoes <= 0 && !magazine ? S.empty : fig,
      title: mixed ? mixText(load, nameOf, false) + S.mix_sep
        + (magazine ? `${num(f.torpedoes)}/${num(magazine)}` : num(f.torpedoes)) : undefined,
      // The wire pays the reload on the map clock; the vital reads it in
      // world time like every other duration on screen (R591).
      wait: reloading ? s.mapToWorldSec(f.reloadSec) : 0,
      loading: reloading ? tubeLoading(f.torpedoes, magazine, f.reloadSec, f.reloadFullSec ?? 0) : 0,
      frac: magazine ? f.torpedoes / magazine : (f.torpedoes > 0 ? 1 : 0),
    };
  }
  if (f.cargoCap > 0) {
    const hold = holdTotal(f.hold);
    return { label: S.v_cargo, ink: 'load', fig: ratioFig(hold, f.cargoCap), frac: hold / f.cargoCap };
  }
  // The Vedette's array is no fraction: full or empty, and the wait (R83).
  if (f.cls === ShipClass.SHIP_CLASS_VEDETTE) {
    return {
      label: S.v_scan, ink: 'load', frac: f.scanReadySec > 0 ? 0 : 1,
      fig: f.scanReadySec > 0 ? dur(s.mapToWorldSec(f.scanReadySec)) : S.ready,
    };
  }
  return null;
}

/**
 * The round pick of an own hull (R2031): the body of the card, between the
 * facts and the order row, for a hull that loads or carries two or more
 * kinds; else undefined. A press sends `setround` with "fleet:kind".
 */
function fleetRoundPick(s: GameState, f: UIFleet, spec: ShipSpec | undefined): string | undefined {
  const torps = s.catalog?.torpedoes ?? [];
  const load = f.rounds ?? [];
  const kinds = pickKinds(loadableKinds(spec, torps), load, roundOrder(torps));
  if (kinds.length < 2) return undefined;
  return roundPickHtml({
    kinds, load, chosen: f.activeRound || f.firingKind, act: 'setround',
    // `n` switches the round off the casino table (R2696).
    nameOf: (k) => s.torpedo(k)?.name ?? '?', idOf: (k) => `${f.id}:${k}`, key: cap('casino_stand'),
    uid: `setround-${f.id}`,
  });
}

/** The readout chip of an own hull that is a guard now, or undefined. */
function guardOf(ctx: MapCardCtx, fleetId: number): GuardChip | undefined {
  const g = ctx.guard(fleetId);
  return g ? guardChip(S.v_refit, g, (sec) => ctx.s.mapToWorldSec(sec)) : undefined;
}

/**
 * The armed state of a card (R2164): the order chip reads the order and
 * PICK (or AIM) on the accent ground, under the title so a long chip never
 * takes the width of the name, and the line under the head says what the
 * next map press does, with CANCEL. Null while nothing is armed. The
 * builder is `armState` of the card kit, which the arcade card takes too.
 */
function armOf(ctx: MapCardCtx, id: number): { status: NonNullable<CardSpec['status']>; armed: ArmLine } | null {
  return armState(ctx.armed(id), cap('escape'));
}

/** Two body sections in order, or undefined when neither stands. */
function joinBody(...parts: (string | undefined)[]): string | undefined {
  const body = parts.filter((p) => p).join('');
  return body || undefined;
}

/**
 * The signature order of an own hull (R2376, R2405): the order row with
 * its press, the state and the recharge clock on the world clock, or null
 * for a hull whose class has no order or whose frame sent no charge. The
 * card draws it in a body section, and the closed context sheet of a
 * telephone draws the row alone (R2415).
 */
function ownSignature(s: GameState, f: UIFleet, spec: ShipSpec | undefined): string | null {
  const a = f.approach;
  const order = spec?.signature ?? SignatureOrder.SIGNATURE_ORDER_NONE;
  if (!a || order === SignatureOrder.SIGNATURE_ORDER_NONE || a.charge === SignatureCharge.SIGNATURE_CHARGE_NONE) return null;
  const rules = s.rules.approach;
  const left = a.active && a.leftSec > 0 ? dur(s.mapToWorldSec(a.leftSec)) : '';
  const classDefault = spec?.signatureTrigger ?? SignatureTrigger.SIGNATURE_TRIGGER_NONE;
  const floor = floorLeftSec(a, s.tacticalScale, Date.now());
  const recharge = signatureRecharge(a, floor > 0 ? dur(s.mapToWorldSec(floor)) : '');
  return signatureRowHtml({ ...signatureAction(f.id, order, a, rules, classDefault, s.rules.command), key: cap('scan') },
    signatureState(a, left), recharge);
}

/**
 * The beam order of an own Beacon (numbers.md §8, R2858): the BEAM press
 * and the state of the beam, in the layout of the signature order row, or
 * null for every other class. A Beacon has no signature order, so the two
 * rows never stand on one card.
 */
function ownBeam(s: GameState, f: UIFleet): string | null {
  if (f.cls !== ShipClass.SHIP_CLASS_BEACON) return null;
  const full = s.catalog?.beaconBeamSec ?? 0;
  const left = f.beamLeftSec > 0 ? dur(s.mapToWorldSec(f.beamLeftSec)) : '';
  return beamRowHtml({ ...beamAction(f.id, f, full > 0 ? dur(s.mapToWorldSec(full)) : ''), key: cap('scan') },
    beamState(f, left));
}

/**
 * The command ship lines of an own hull (v2-the-deep.md §15.7, §16.7,
 * R2617). The FORMATION line stands under the GUNS line while the
 * formation of a command ship holds, with the count of its members; a
 * member names the command ship it keeps its station by. A command ship
 * states its AURA and its MUSTER: on with a captain aboard, off with none.
 * The reach and the share of the aura and the reach and the pace of
 * MUSTER are the figures of `CatalogRules.command` (R2628), which every
 * hello of a Conquest world carries (R2805); with no figures the two lines
 * stand out. An arcade match has no captains: there the catalog states
 * the flat lift (`arcadeLiftPct`, R3218), and AURA and MUSTER are on with
 * no captain, AURA with the lift. The arcade selection card reads the same
 * lines, so the context is the state and the own fleets alone.
 */
export function commandFacts(ctx: Pick<MapCardCtx, 's' | 'fleets'>, f: UIFleet): Fact[] {
  const facts: Fact[] = [];
  if (f.formation) {
    const n = ctx.fleets().filter((m) => m.formationOf === f.id).length;
    facts.push({ key: S.f_formation, val: esc(S.formation_on({ n })), title: S.formation_title });
  } else if (f.formationOf) {
    const cmd = ctx.fleets().find((m) => m.id === f.formationOf);
    if (cmd) facts.push({ key: S.f_formation, val: esc(S.formation_member({ name: cmd.name })), title: S.formation_title });
  }
  const c = ctx.s.rules.command;
  if (f.cls === ShipClass.SHIP_CLASS_COMMODORE && c) {
    const lift = c.arcadeLiftPct ?? 0;
    const on = f.captainId !== 0 || lift > 0;
    const aura = lift > 0 ? S.aura_lift({ pct: lift, px: c.auraReachPx })
      : on ? S.aura_on({ px: c.auraReachPx, pct: Math.round(c.auraShare * 100) }) : S.aura_off;
    facts.push({ key: S.f_aura, title: S.aura_title, val: esc(aura) });
    facts.push({
      key: S.f_muster, title: S.muster_title,
      val: esc(on ? S.muster_on({ px: c.musterReachPx, mul: Math.round(c.musterBoostMul * 100) / 100 }) : S.aura_off),
    });
  }
  return facts;
}

/** The points of one attribute as the COMMAND chip writes them: whole, or
 *  one place for a half point of the share. */
function auraPts(n: number): string {
  return String(Math.round(n * 10) / 10);
}

/**
 * The figure and the title of the COMMAND chip of an own hull (R2732,
 * `ui-map-interface.md` §8 "The conditions row"). The server sends the
 * chip while the command aura lifts the hull; the client names the points
 * of each attribute the aura lifts: the higher of the own captain's points
 * and `CatalogRules.command.auraShare` of the command ship captain's. An
 * empire holds one command ship (`ShipSpec.cap`), so the source is the own
 * Commodore with a captain nearest the hull. null when a figure is unknown.
 * Under the flat lift of an arcade match (`arcadeLiftPct`, R3218) the
 * source needs no captain, and the figure is the lift: "+15 %".
 */
export function auraFigure(ctx: Pick<MapCardCtx, 's' | 'fleets'>, f: UIFleet): { value: string; why: string } | null {
  const c = ctx.s.rules.command;
  if (!c) return null;
  const lift = c.arcadeLiftPct ?? 0;
  let src: UIFleet | undefined;
  let best = Infinity;
  for (const m of ctx.fleets()) {
    if (m.id === f.id || m.cls !== ShipClass.SHIP_CLASS_COMMODORE || (m.captainId === 0 && lift === 0)) continue;
    const d = (m.x - f.x) * (m.x - f.x) + (m.y - f.y) * (m.y - f.y);
    if (d < best) { best = d; src = m; }
  }
  if (src && lift > 0) {
    return { value: S.cond_lift({ pct: lift }), why: S.cond_command_lift_why({ name: src.name, pct: lift }) };
  }
  const cap = src ? ctx.s.captain(src.captainId) : undefined;
  if (!src || !cap) return null;
  const own = f.captainId ? ctx.s.captain(f.captainId)?.attrs ?? [] : [];
  const parts: string[] = [];
  for (const a of c.auraAttrs) {
    const lift = (cap.attrs[a] ?? 0) * c.auraShare;
    if (lift > (own[a] ?? 0)) parts.push(S.cond_aura_pts({ short: OS.attr_short[a] ?? '', pts: auraPts(lift) }));
  }
  return {
    value: parts.join(S.cond_aura_sep),
    why: S.cond_command_why({ name: src.name, pct: Math.round(c.auraShare * 100) }),
  };
}

/**
 * The route profile of an own course (v2 phase 2, round 088 card 05,
 * R2616): the bands along the legs against the depth rating of the hull,
 * while the hello carries the band field. The time to the rating reads
 * the class speed on the map clock, in the card's clock form.
 */
function ownRouteProfile(s: GameState, f: CardFleet, spec: ShipSpec | undefined): string | undefined {
  const depth = s.depthBands;
  const pts = depth ? coursePoints(f.x, f.y, f.course) : null;
  if (!depth || !pts) return undefined;
  const speed = spec?.speedUh ?? 0;
  return routeProfileHtml(routeProfile(depth, pts, ratingOf(f, spec)),
    (px) => (speed > 0 ? dur(s.mapToWorldSec((px / speed) * 3600)) : ''));
}

/**
 * The depth rating of an own hull (R2625, R2637): the rating the server
 * sends for the fleet, which counts R4, else the class rating of its ship
 * row. The client mirrors no part of the rule.
 */
export function ratingOf(f: { depthRating?: number }, spec: ShipSpec | undefined): number {
  return f.depthRating || (spec?.depthRating ?? 0);
}

/** The CAPTAIN fact of an own hull as plain text, at sea and in the pens
 *  (the deploy ask): the name and the phase, or the way to board one.
 *  null for a support hull, which carries no officer (R385). */
export function captainFact(s: GameState, captainId: number, support: boolean): string | null {
  if (support) return null;
  const cap = s.captain(captainId);
  return cap ? S.captain({ name: cap.name, phase: cap.phase }) : S.no_captain;
}

/** The card of one of the reader's own fleets (084 card 04, R1809). */
function ownFleetCard(ctx: MapCardCtx, f: CardFleet, size: CardSize): CardSpec {
  const s = ctx.s;
  const spec = s.ship(f.cls);
  const vitals = hullShield(f, f.hullMax || spec?.hull, f.shieldMax || spec?.shield, regenWaitSec(s, f));
  // Readiness, where the wire sent it, with the floor as a tick (R769).
  if (f.readinessPct > 0) {
    vitals.push({
      label: S.v_ready, fig: pctFig(f.readinessPct), frac: f.readinessPct / 100, ink: 'ready',
      tick: f.readinessFloorPct > 0 ? f.readinessFloorPct / 100 : undefined,
    });
  }
  const load = loadVital(s, f, spec);
  if (load) vitals.push(load);
  // The reader's own hull states no faction and no clan: both are the
  // reader's own.
  // A STEER of the helm keys states its numbers alone (R2707), and the
  // telegraph step as its word (R2718).
  const helm = f.mission === Mission.MISSION_STEER ? ctx.helm?.(f.id) ?? null : null;
  const order = helm
    ? S.order_steer_figs({ order: missionLabel(f), deg: helm.headingDeg, step: telegraphWord(helm.step) })
    : missionLabel(f);
  const facts: Fact[] = [{ key: S.f_order, val: esc(order) }];
  // The work of a SEARCH (v2 §9, R2649): the time left at the
  // point, or the word of a fleet that still sails there.
  const work = workFact(s, f);
  if (work) facts.push(work);
  // The echo line of the search hint (chart card 33, R2649).
  const echo = echoFact(s, f);
  if (echo) facts.push(echo);
  // The compressed cargo of the voyage home (v2 §9.6, R2834).
  const compressed = compressedFact(s, f.id);
  if (compressed) facts.push(compressed);
  // The two Sapper timers of the wire (R2654): the next LAY and the loud lay.
  const sapper = isSapperCard(s, f.cls);
  const lastLay = ctx.lastLay?.(f.id) ?? 0;
  if (sapper) facts.push(...sapperFacts(s, f));
  const captain = spec ? captainFact(s, f.captainId, spec.support) : null;
  if (captain !== null) facts.push({ key: S.f_captain, val: esc(captain) });
  // The fit strip (R1501): no row for a basic hull.
  const strip = fitStrip(s.catalog, { weapType: f.weapType, shieldType: f.shieldType, fit: s.fleetFit(f.id) });
  if (strip) facts.push({ key: S.f_systems, val: strip });
  // The live noise (R1500), the class figure when the field was not sent.
  facts.push({
    key: S.f_speed, val: esc(S.speed({
      speed: speedPxMin(spec?.speedUh ?? 0, f.deep, s.tacticalScale),
      noise: liveNoise(f.noiseMilli, spec?.noise ?? 0).toFixed(2),
    })),
  });
  // The band of the hull and its depth rating while the hello carries the
  // band field (v2 phase 2, R2616); a trench is the abyss, and its chip
  // stands in the conditions row then. Else the trench line of today.
  const band = depthFact(s.depthBands, f.x, f.y, ratingOf(f, spec));
  if (band) facts.push({ key: S.f_depth, val: esc(band) });
  else if (f.deep) facts.push({ key: S.f_depth, val: esc(S.deep) });
  if (f.deepRun) facts.push({ key: S.f_gait, val: esc(S.deep_run) });
  if (f.holdFire) facts.push({ key: S.f_guns, val: esc(S.hold_fire_on), title: S.hold_fire_title });
  facts.push(...commandFacts(ctx, f));
  if (f.shaft) facts.push({ key: S.f_working, val: esc(S.shaft) });
  if (f.cls === ShipClass.SHIP_CLASS_DREDGER && (s.empire?.dredgerBrain ?? false)) {
    facts.push({ key: S.f_next_field, val: esc(f.autoFieldOff ? S.auto_field_off : S.auto_field_on) });
  }
  if (f.slowed) facts.push({ key: S.f_status, val: esc(S.slowed) });
  // The approach (R2376): HEARD or the break contact (R2458), and the
  // signature order. The server sets `quiet` only while the shield mends.
  const approach = f.approach;
  if (f.heard) facts.push({ key: S.f_sonar, val: esc(S.sonar_heard), title: S.heard_title });
  else if (approach?.quiet) facts.push({ key: S.f_sonar, val: esc(S.quiet_mending) });
  const sig = (approach ? ownSignature(s, f, spec) : null) ?? ownBeam(s, f);
  // The on-ramp offer (R3075): it stands over the signature row, and it
  // takes the closed sheet, because it lives for a short time after a MOVE.
  const offer = ctx.laneOffer?.(f.id) ?? null;
  const offerRow = offer
    ? laneOfferRowHtml(f.id, offer.lane, dur(s.mapToWorldSec(offer.saveMapSec)), cap('lane'))
    : null;

  const row = orderRow(f.cls, spec?.dps ?? 0, fleetOrderGate(s, f.cls, spec));
  const actions = [orderAction(ctx, f, row.action, 'dark'), ...row.quiet.map((o) => orderAction(ctx, f, o, 'quiet'))];
  const held = ctx.groupOf(f.id);
  const items: Action[] = row.more.map((o) => orderAction(ctx, f, o, 'quiet'));
  // CACHES opens the cache sheet of the fleet (v2 §9.3, R2649).
  const caches = cachesItem(s, f.id);
  if (caches) items.push(caches);
  // ⋯ holds the orders alone, and EDIT in the head holds the group slots
  // and the name on its own (R1845, R2008). The group of a fleet is a
  // saved value, so the slots are a compact pill group of the choice kit
  // with the slot number as the label (R2011): the id is "fleet:slot", a
  // press assigns, and a press on the lit slot empties it.
  const slots: Choice[] = [];
  for (let n = 1; n <= GROUP_SLOTS; n++) {
    slots.push({ id: `${f.id}:${n}`, label: String(n), title: S.group_slot({ n }) });
  }
  const sections: MoreSection[] = [{
    head: S.more_groups, items: [],
    pick: pillGroup(slots, held ? `${f.id}:${held}` : '', 'assign', { label: S.more_groups, size: 'compact' }),
  }];
  if (ctx.canRename) {
    sections.push({ head: S.more_name, items: [{ act: 'rename', id: f.id, face: 'quiet', label: S.o_rename, title: S.rename_title }] });
  }
  const arm = armOf(ctx, f.id);
  // The conditions row (v2 slot 1, R2576): the wire sends the list while
  // `modifiers`, `depth` or `currents` (R2609) is latched, and the COMMAND
  // row alone in every other world that plays the command ship (R2732,
  // R2805), so the control reads the list alone; an empty list draws no
  // row. PRESSURE
  // writes the hull a wall minute it costs, the minute of the speed fact
  // (R2616).
  const aura = auraFigure(ctx, f);
  const conds = condsOf(f.conditions, (sec) => dur(s.mapToWorldSec(sec)), {
    wearPerMin: (f.hullMax || spec?.hull || 0) * s.rules.pressurePerMapMinute * s.tacticalScale,
    ...(aura ? { aura } : {}),
  });
  // The last reason the hull stopped or turned (R3265): the words of the
  // newest notice that named it, until a new order.
  const why = s.fleetWhy(f.id);
  return {
    size, mark: markSvg('fleet', cssColor(f.faction)), title: f.name, ink: { faction: f.faction },
    ...(conds.length > 0 ? { conds } : {}),
    ...(why ? { note: { lines: [esc(why)] } } : {}),
    titleHtml: held ? groupChipHtml(held, f.faction) : '',
    kind: spec?.name ?? S.unknown, status: arm?.status ?? { text: orderChip(s, f), tone: 'on' }, close: true,
    statusOnKind: arm ? true : undefined, armed: arm?.armed,
    vitals, facts, body: joinBody(offerRow ? signatureSection(offerRow) : undefined,
      sig ? signatureSection(sig) : undefined, fleetRoundPick(s, f, spec),
      // The lay pick, the twin of the round pick, on the same key (R2710).
      sapper ? layPickHtml(s, f, lastLay, cap('casino_stand')) : undefined,
      sapper ? stowSectionHtml(s, f, spec, lastLay, { keycaps: false }) : undefined,
      ownRouteProfile(s, f, spec)), actions,
    closedRow: offerRow ?? sig ?? undefined,
    more: { open: ctx.moreOpen(f.id), items },
    // A set Sentinel with the Refit is a guard (R2080): its clock, as the
    // map's pip states it.
    guard: guardOf(ctx, f.id),
    edit: { open: ctx.editOpen(f.id), title: ctx.canRename ? S.edit_title : S.edit_group_title, sections },
  };
}

// --- a friendly fleet and a contact (084 card 05) ------------------------

/** The faction row and, for a commander of the reader's own clan, the clan
 *  row. The wire names no clan of another commander, so a hull of the
 *  faction outside the clan states none. */
function allegiance(s: GameState, f: CardFleet, owner: number): Fact[] {
  const facts: Fact[] = [{ key: S.f_faction, val: esc(factionName(f.faction)) }];
  const clan = s.clan;
  if (clan && clan.tag && clan.members.some((m) => m.accountId === owner)) {
    facts.push({ key: S.f_clan, val: esc(clan.tag) });
  }
  return facts;
}

/** A hull of the reader's faction that another commander owns. */
function friendFleetCard(ctx: MapCardCtx, f: CardFleet, size: CardSize): CardSpec {
  const spec = ctx.s.ship(f.cls);
  const facts: Fact[] = allegiance(ctx.s, f, f.ownerPlayer);
  if (f.ownerName) {
    facts.push({ key: S.f_commander, val: esc(f.ownerName) + ctx.commanderMark(f.ownerPlayer, f.ownerName) });
  }
  facts.push({ key: S.f_order, val: esc(MISSION_NAMES[f.mission] ?? '—') });
  if (ctx.squadmate(f.ownerPlayer)) facts.push({ key: S.f_squad, val: esc(S.squad_member) });
  return {
    size, mark: markSvg('fleet', cssColor(f.faction)), title: f.name, ink: { faction: f.faction },
    titleHtml: ctx.hullMark(f), kind: spec?.name ?? S.unknown,
    status: f.mission !== Mission.MISSION_UNSPECIFIED ? { text: MISSION_NAMES[f.mission] ?? '' } : undefined,
    close: true, vitals: hullShield(f), facts,
  };
}

/**
 * A hull of another faction: a bearing. The split is by faction and never
 * by `classified` (R198). Its systems and its commander stand while a
 * scan reveal holds it (R412, R1436), and its hull and shield where the
 * gate of R425 sent them; else the vitals are the hatched "?".
 *
 * The chip states the age of the scan from the server's `scan_left_sec`,
 * and over the last share of the reveal the hull and shield bars fade back
 * to the hatched "?" (round 084 card 05, R1837). The card cannot tell the
 * reveal from the gate of R425, so the bars fade under both; the frame at
 * the end of the reveal states which gate still holds them.
 */
export function contactCard(ctx: MapCardCtx, f: CardFleet, size: CardSize): CardSpec {
  const s = ctx.s;
  const spec = f.classified ? undefined : s.ship(f.cls);
  // The reveal is a nonzero type, or the scan clock of the state: a hull
  // with no typed systems carries both types at 0 under a reveal too
  // (R2457). The cold cache keeps an owner name after the reveal ends, so
  // the owner reads under the reveal test only.
  const revealed = f.weapType !== 0 || f.shieldType !== 0
    || (s.scanSeen.leftSec(f.id, Date.now()) ?? 0) > 0;
  const look = scanLook(revealed ? ctx.scanLeftSec(f.id) : null, s.rules.scanRevealSec, SCAN.fadeShare);
  const facts: Fact[] = [];
  // The approach (R2376): the aspect leads, so the peek keeps it. It shows
  // in combat alone, while the hull draws its chevrons (R2457). It reads
  // the contact's heading and charge alone; the own-only fields of a
  // contact are never read.
  const approach = f.approach;
  const aspect = aspectFact(ctx, f);
  if (aspect) facts.push(aspect);
  // A commander a scan named comes before the faction, so the peek keeps
  // the name (R2457); an unknown commander comes after it, so the peek
  // keeps the faction beside the aspect (R2480).
  const faction: Fact = { key: S.f_faction, val: esc(factionName(f.faction)) };
  if (revealed && f.ownerName) facts.push({ key: S.f_commander, val: esc(f.ownerName) }, faction);
  else facts.push(faction, { key: S.f_commander, val: esc(S.commander_unknown) });
  if (revealed) {
    const strip = fitStrip(s.catalog, { weapType: f.weapType, shieldType: f.shieldType, fit: [] });
    if (strip) facts.push({ key: S.f_systems, val: strip });
  }
  const charge = approach ? chargeText(approach.charge, spec?.signature ?? SignatureOrder.SIGNATURE_ORDER_NONE) : '';
  if (charge) facts.push({ key: S.f_signature, val: esc(charge) });
  return {
    size, mark: markSvg('contact', cssColor(f.faction)), title: S.contact_title, ink: { faction: f.faction },
    kind: spec?.name ?? S.kind_unclassified,
    status: revealed
      ? { text: look.ageSec === null ? S.st_scanned : S.st_scanned_age({ age: dur(s.mapToWorldSec(look.ageSec)) }), tone: 'on' }
      : { text: S.st_heard },
    close: true,
    vitals: f.hullPct > 0 && look.fade > 0 ? hullShield(f, undefined, undefined, 0, look.fade) : unknownHullShield(),
    facts,
    note: revealed ? undefined : { lines: [esc(S.note_contact)] },
    // MARK opens the marker menu on the cell of the contact (R2002).
    actions: markActions(ctx, f),
  };
}

/** The ASPECT fact of a hull of another side (R2376): it shows in combat
 *  alone, while the hull draws its chevrons (R2457). Null for no aspect. */
function aspectFact(ctx: MapCardCtx, f: CardFleet): Fact | null {
  const approach = f.approach;
  if (!approach || !ctx.chevronsShown(f.id)) return null;
  const aspect = contactAspect(ctx, f, approach.headingMrad);
  return aspect === HitAspect.HIT_ASPECT_NONE ? null
    : { key: S.f_aspect, val: esc(aspectWord(aspect)), title: S.aspect_title };
}

/** MARK on a hull of another side: it opens the marker menu on the cell
 *  of the hull (R2002). None on a page that places no marker. */
function markActions(ctx: MapCardCtx, f: CardFleet): Action[] | undefined {
  return ctx.canMark ? [{ act: 'markcontact', id: f.id, label: MS.mark, face: 'quiet' }] : undefined;
}

/**
 * The aspect a contact shows the reader (R2376): the arc that faces the
 * selected own hull, else the nearest own hull. NONE when the reader has no
 * hull at sea or the hello carried no rules.
 */
function contactAspect(ctx: MapCardCtx, f: CardFleet, headingMrad: number): HitAspect {
  const rules = ctx.s.rules.approach;
  if (!rules) return HitAspect.HIT_ASPECT_NONE;
  const sel = ctx.s.selection?.kind === 'fleet' ? ctx.s.selection.id : 0;
  const from = aspectRef(ctx.fleets(), sel, f);
  return from ? aspectOf(headingMrad, f, from, rules) : HitAspect.HIT_ASPECT_NONE;
}

/**
 * The class word of a hull (R2637): the catalog name, the Gulper by its
 * locale word (an `npc` row that no catalog lists, R2622), else the word
 * of a withheld class.
 */
export function classWord(s: GameState, cls: ShipClass): string {
  const kind = monsterKindOf(cls);
  if (kind >= 0) return MONSTER_CLASS_WORDS[kind];
  return s.ship(cls)?.name ?? S.kind_unclassified;
}

/** The word of what a monster does now (R2719), or '' for no act. */
export function actWord(act: NpcAct): string {
  switch (act) {
    case NpcAct.NPC_ACT_IDLE: return S.npc_act_idle;
    case NpcAct.NPC_ACT_MOVE: return S.npc_act_move;
    case NpcAct.NPC_ACT_TELL: return S.npc_act_tell;
    case NpcAct.NPC_ACT_STRIKE: return S.npc_act_strike;
    case NpcAct.NPC_ACT_HOLD: return S.npc_act_hold;
    case NpcAct.NPC_ACT_LAUNCH: return S.npc_act_launch;
    case NpcAct.NPC_ACT_PULSE: return S.npc_act_pulse;
    case NpcAct.NPC_ACT_GATHER: return S.npc_act_gather;
    case NpcAct.NPC_ACT_SCATTER: return S.npc_act_scatter;
    default: return '';
  }
}

/**
 * The facts of a monster (round 109, R2719): the name of its design, and
 * what it does now, as the monster layer reads the wire.
 */
function monsterFacts(ctx: MapCardCtx, f: CardFleet, facts: Fact[]): void {
  const m = ctx.monster?.(f.id) ?? null;
  if (!m) return;
  const design = monsterDesignWord(m.kind, m.design);
  if (design) facts.push({ key: S.f_design, val: esc(design) });
  const act = actWord(m.act);
  if (act) facts.push({ key: S.f_act, val: esc(act) });
}

/**
 * A hull of the NPC side (v2 §8, round 088 card 11, R2637): a pirate
 * group or a monster. It reads no commander and no faction, because no
 * player commands it: the title names the kind, the class line reads the
 * class the wire sends, the side fact names the NPC side, and the head
 * and the title wear the NPC ink. The vitals follow the gate of a
 * contact (R425), and MARK stands as on a contact.
 */
export function npcCard(ctx: MapCardCtx, f: CardFleet, size: CardSize): CardSpec {
  const s = ctx.s;
  const monster = f.npc === NpcKind.NPC_KIND_MONSTER;
  const facts: Fact[] = [];
  const aspect = aspectFact(ctx, f);
  if (aspect) facts.push(aspect);
  // The fact is plain text: no card colours an NPC fact in the shield
  // ink, which is the ink of a shield bar too (v2 §18.2 slot 2).
  facts.push({ key: S.f_side, val: esc(S.npc_side) });
  if (monster) monsterFacts(ctx, f, facts);
  const card: CardSpec = {
    size, mark: markSvg(monster ? 'monster' : 'pirate', 'var(--npc)'),
    title: monster ? S.npc_monster : S.npc_pirate, ink: { npc: true },
    kind: classWord(s, f.cls),
    status: { text: S.st_heard },
    close: true,
    vitals: f.hullPct > 0 ? hullShield(f) : unknownHullShield(),
    facts,
    note: { lines: [esc(monster ? S.npc_monster_note : S.npc_pirate_note)] },
    actions: markActions(ctx, f),
  };
  if (!monster) pirateCard(card, s, f);
  return card;
}

/**
 * The pirate lines of an NPC card (v2 §8.5, §8.6, R2720, R2721): the
 * brotherhood of the group, the taken hull, and the boarding while it runs.
 * A support hull of the NPC side is a hull that a pirate group took, so it
 * reads TAKEN HULL with its class (ruling R-h) and the note of the take.
 */
function pirateCard(card: CardSpec, s: GameState, f: CardFleet): void {
  const word = f.brotherhood ? BROTHERHOOD_WORDS[f.brotherhood] : undefined;
  if (word) card.facts!.push({ key: S.f_brotherhood, val: esc(word) });
  if (s.ship(f.cls)?.support) {
    card.title = S.taken_hull;
    card.note = { lines: [esc(S.taken_hull_note)] };
  }
  if (f.boardingTarget) card.status = { text: S.boarding_status, tone: 'amber' };
}

/** The card of a fleet the reader selected or points at. */
export function fleetCard(ctx: MapCardCtx, f: CardFleet | null, size: CardSize): CardSpec {
  if (!f) return { size, title: S.contact_lost, ink: 'neutral', close: true };
  if (f.npc) return npcCard(ctx, f, size);
  if (f.faction !== ctx.s.faction) return contactCard(ctx, f, size);
  return f.own ? ownFleetCard(ctx, f, size) : friendFleetCard(ctx, f, size);
}

// --- a hold (084 card 06) -------------------------------------------------

/** The rounds of one kind the hold holds in the shared stock. */
function heldOf(s: GameState, kind: number): number {
  return s.empire?.torpedoes.find((t) => t.kind === kind)?.count ?? 0;
}

/** The reader's own hold: the magazine, the tubes and the point defence. */
function ownHoldCard(ctx: MapCardCtx, site: Site, size: CardSize): CardSpec {
  const s = ctx.s;
  const banks = s.level(Building.BUILDING_TORPEDOBANKS);
  const facts: Fact[] = [];
  const reach = s.empire?.sonarRange ?? 0;
  if (reach > 0) facts.push({ key: S.f_sonar, val: esc(pxFig(reach)) });
  // The harbour plan is a section of this card (round 086 card 07 b,
  // R1824): one card for one selected hold, under the round pick.
  const plan = harbourPlanHtml(s, ctx.historical);
  const spec: CardSpec = {
    size, mark: markSvg('hold', cssColor(site.faction)), title: site.name, ink: { faction: site.faction },
    kind: S.kind_hold_own, close: true, facts, body: plan || undefined,
  };
  if (banks === 0) {
    spec.note = { lines: [esc(S.note_needs_banks)] };
    spec.actions = [buildHere()];
    return spec;
  }
  const stocked = s.stockedHoldRounds();
  const total = stocked.reduce((n, t) => n + heldOf(s, t.id), 0);
  const wait = Math.ceil(s.baseTubeReadySec());
  const ready = s.baseTubesReady();
  const pd = Math.ceil(s.pointDefenceReadySec());
  spec.vitals = [
    { label: S.v_magazine, ink: 'store', fig: total > 0 ? num(total) : S.empty, frac: total > 0 ? 1 : 0 },
    // Manual fire is per tube (R88): a shot puts that tube out for a while.
    {
      label: S.v_tubes, ink: 'load', fig: ratioFig(wait > 0 ? 0 : ready, banks), frac: (wait > 0 ? 0 : ready) / banks,
      wait,
    },
    // Point defence (R86): below the gate level the vital names the level
    // it starts at (R1154).
    !s.pointDefenceArmed()
      ? { label: S.v_point_def, ink: 'ready', fig: S.pd_from({ level: s.rules.torpedobankPointDefenceLevel }), frac: 0 }
      : { label: S.v_point_def, ink: 'ready', fig: pd > 0 ? S.pd_wait({ sec: pd }) : S.ready, frac: pd > 0 ? 0 : 1 },
  ];
  // The round to fire, when more than one type is stocked: the chooser
  // of the choice kit in its compact size, with the stock as the count of
  // each type (R2011). The base rounds stand first, then the ship rounds
  // the hold fires from the shared stock (R2779 rule 10, R2783), and `n`
  // steps the pick as it steps the round of a hull. One type is a fact.
  if (stocked.length > 1) {
    spec.body = roundPickHtml({
      kinds: stocked.map((t) => t.id), load: stocked.map((t) => ({ kind: t.id, count: heldOf(s, t.id) })),
      chosen: ctx.baseTorp, act: 'basetorp', nameOf: (k) => s.torpedo(k)?.name ?? '?', idOf: String,
      key: cap('casino_stand'), uid: 'basetorp',
    }) + plan;
  } else if (stocked.length === 1) {
    facts.push({ key: S.f_round, val: esc(S.round_pick({ name: stocked[0].name, held: heldOf(s, stocked[0].id) })) });
  }
  // The armed aim of the hold's battery (R2164).
  const arm = armOf(ctx, 0);
  if (arm) {
    spec.status = arm.status;
    spec.statusOnKind = true;
    spec.armed = arm.armed;
  }
  spec.actions = [{
    act: 'aim', id: 0, face: 'dark', key: cap('base_tubes'), on: ctx.aiming === 0, off: stocked.length === 0,
    label: ctx.aiming === 0 ? S.o_aiming : S.o_fire_base,
  }, buildHere()];
  if (stocked.length === 0) spec.note = { lines: [esc(S.note_no_rounds)] };
  return spec;
}

/** BUILD HERE on the own hold card (085 card 03, R1829): a quiet press that
 *  opens BUILD · BASE, the one complete list. The card shows no build row. */
const buildHere = (): Action => ({ act: 'buildhere', face: 'quiet', label: S.o_build_here });

/** The mark ink and the title ink of a site: its faction, or the text ink
 *  of a site nobody holds. */
function siteInks(faction: number): { mark: string; ink: CardSpec['ink'] } {
  return faction === 0 ? { mark: 'var(--text)', ink: 'neutral' } : { mark: cssColor(faction), ink: { faction } };
}

/**
 * Another commander's hold, a faction installation, or an unknown site.
 * The magazine of another hold is the hatched "?": the wire states none.
 */
function otherSiteCard(ctx: MapCardCtx, site: Site, size: CardSize, sonar: number): CardSpec {
  const s = ctx.s;
  const hold = site.kind === SiteKind.SITE_KIND_PLAYER_BASE;
  const facts: Fact[] = [];
  if (hold && site.commander) {
    facts.push({ key: S.f_commander, val: esc(site.commander) + ctx.commanderMark(site.ownerPlayer, site.commander) });
  } else {
    facts.push(heldByFact(site.faction));
  }
  // A squadmate's hold wears the half plate on the map (§6.2); the card
  // says the same thing in words (R199).
  if (site.ownerPlayer !== s.playerId && ctx.squadmate(site.ownerPlayer)) {
    facts.push({ key: S.f_squad, val: esc(S.squad_member) });
  }
  // The reach the server gave, never a guessed one (R112).
  if (sonar > 0) facts.push({ key: S.f_sonar, val: esc(pxFig(sonar)) });
  const unknown: Vital = { label: '', fig: '', frac: 0, unknown: true };
  const inks = siteInks(site.faction);
  return {
    size, mark: markSvg(hold ? 'hold' : 'inst', inks.mark),
    title: site.name || siteKindName(site.kind),
    ink: inks.ink,
    kind: hold ? S.kind_hold({ faction: factionName(site.faction) }) : siteKindName(site.kind),
    close: true,
    vitals: hold ? [
      { ...unknown, label: S.v_magazine }, { ...unknown, label: S.v_tubes }, { ...unknown, label: S.v_point_def },
    ] : undefined,
    facts,
  };
}

/** The word of a site kind. The wire's kind enum counts from 1. */
function siteKindName(kind: number): string {
  return OS.site_kinds[kind - 1] ?? S.unknown;
}

// --- a strategic object (084 card 07) -------------------------------------

/**
 * The two pickers of one pledged hull as two pill groups of the choice kit
 * in the compact size (R796, R2011, `numbers.md` §8). The id of a pill is
 * "fleet:plan", and the plan is the WHOLE packed plan, so one handler
 * serves both rows. The current pick is lit.
 */
function planPickHtml(f: PledgedFleet): string {
  const pos = f.battlePlan & PLAN_POS_MASK;
  const beh = f.battlePlan >> PLAN_BEH_SHIFT;
  const seg = (list: [number, string][], current: number, pack: (v: number) => number) => pillGroup(
    list.map(([v, label]) => ({ id: `${f.fleetId}:${pack(v)}`, label })),
    `${f.fleetId}:${pack(current)}`, 'setplan', { size: 'compact' });
  return `<div class="ck-sect"><span class="ck-cap">${OS.structures_plan_head({ fleet: esc(f.name) })}</span>`
    + seg(PLAN_POSITIONS, pos, (v) => v | (beh << PLAN_BEH_SHIFT))
    + seg(PLAN_BEHAVIOURS, beh, (v) => pos | (v << PLAN_BEH_SHIFT)) + '</div>';
}

/** A strategic object: its gauge, its window and its pledges (R787, R794). */
function objectCard(ctx: MapCardCtx, site: Site, size: CardSize): CardSpec {
  const s = ctx.s;
  const g = s.siegeOn(site.id);
  const facts: Fact[] = [heldByFact(site.faction)];
  // The wire name is "Kind · Place": the title is the place, and the kind
  // line states the kind once (R733, R1879). With no siege row the client
  // does not know the kind, so the title keeps the whole name.
  const title = g?.kindName ? sitePlace({ siteName: site.name, kindName: g.kindName }) : site.name;
  // A squadmate's object (R199) reads as a squadmate's hold does.
  const inks = siteInks(site.faction);
  const spec: CardSpec = {
    size, mark: markSvg('object', inks.mark),
    title: title || siteKindName(site.kind),
    ink: inks.ink,
    kind: g?.kindName || S.object_default, close: true, facts,
    // The chip WINDOW hh:00 is long: it stands under the name (R1879).
    statusOnKind: true,
    // The featured object of the day carries its tag after the name (R3272).
    titleHtml: g?.featured ? featuredTagHtml() : undefined,
  };
  if (g) {
    // The gauge in the vital idiom, against the cap the catalog states
    // (084 open point 8); no dial and no bare number.
    const max = s.rules.gaugeMax;
    const gauge = g.gauge10 / 10;
    const inSec = s.windowInSec(g.windowHour);
    const open = inSec < 0;
    const span = s.rules.objWindowSec;
    // The live window, off the world clock: a Sieges frame arrives on
    // three edges only.
    const left = open && span > 0 ? Math.max(0, span + inSec) : 0;
    spec.status = open ? { text: S.st_open, tone: 'amber' } : { text: S.st_window({ hour: pad2(g.windowHour) }) };
    spec.vitals = [
      gaugeVital(gauge, max),
      {
        label: S.v_window, ink: 'ready', frac: open && span > 0 ? left / span : 0,
        fig: open ? S.window_open : S.window_in({ left: dur(inSec) }),
      },
      // The pledge secrecy rule (R794): the reader's own faction's count.
      { label: S.v_pledges, ink: 'gauge', fig: String(g.ownCommits), frac: g.ownCommits > 0 ? 1 : 0 },
    ];
    const gain = benefitText(g.kind, g.benefit, g.benefit2);
    if (gain) facts.push({ key: S.f_grants, val: esc(benefitShort(g.kind, g.benefit, g.benefit2)), title: gain });
    // Every other faction is a flag, never a count (R794).
    facts.push({
      key: S.f_contested, val: esc(g.enemyPledged ? S.contested_yes : S.contested_no),
      title: g.enemyPledged ? S.contested_title : undefined,
    });
    if (g.youCommitted) facts.push({ key: S.f_your_pledge, val: esc(S.pledge_stands) });
    // A won window holds the gauge until the next close (R1449, R1454).
    if (g.healBlocked) facts.push({ key: S.f_heal, val: esc(S.heal_blocked) });
  }
  // The battle plan of each own pledged hull (R796). A pledged hull is off
  // the map (R76), so this card is the one place that carries its id.
  if (g && g.yourFleets.length > 0) {
    spec.body = g.yourFleets.map(planPickHtml).join('');
  }
  // COMMIT sends the fleet the reader selected last; while the object is
  // the selection, no own fleet is (R787, R788).
  const sel = s.selection?.kind === 'fleet' ? s.selection.id : 0;
  const fleet = sel ? ctx.fleets().find((f) => f.id === sel) : undefined;
  if (!fleet) {
    spec.note = { lines: [esc(S.note_commit_hint)] };
    return spec;
  }
  const open = g ? s.windowOpen(g.windowHour) : false;
  const mine = site.faction === s.faction;
  spec.actions = [{
    act: 'commit', id: site.id, n: fleet.id, face: 'dark', off: !open,
    label: S.commit({ verb: mine ? S.o_defend : S.o_commit, fleet: fleet.name }),
  }];
  if (!open) spec.note = { lines: [esc(S.note_window_shut)] };
  return spec;
}

// --- a deposit (084 card 08) ----------------------------------------------

/** A deposit: the reserve of its first fill, the yield, SEND HAULER. */
function depositCard(ctx: MapCardCtx, site: Site, size: CardSize): CardSpec {
  const resource = OS.deposit_names[site.resource - 1] ?? S.unknown;
  // A worked-out field one of your own haulers still cuts reads as a
  // shaft (Deep Mining, I2, R715): the fleet carries the flag.
  const shafted = site.amount === 0 && ctx.fleets().some((f) => f.shaft && cellAt(f.x, f.y) === site.cell);
  const spec: CardSpec = {
    size, mark: markSvg('deposit', 'var(--text)'), title: site.name, ink: 'neutral',
    kind: S.kind_deposit({ resource }), close: true,
    vitals: [{
      label: S.v_reserve, ink: 'store', frac: site.capacity > 0 ? site.amount / site.capacity : (site.amount > 0 ? 1 : 0),
      fig: site.capacity > 0 ? ratioFig(site.amount, site.capacity) : num(site.amount),
    }],
    facts: [{ key: S.f_yield, val: esc(resource) }],
  };
  if (site.amount === 0) spec.note = { lines: [esc(shafted ? S.shafted : S.worked_out)] };
  const send = sendHauler(ctx, site.cell);
  if (send) spec.actions = [send];
  return spec;
}

/**
 * SEND HAULER on the card of a gather field, or null with no own Dredger
 * at sea: a GATHER of the first own Dredger to the field on `cell`. A
 * deposit and a vent take the one press (R3147).
 */
function sendHauler(ctx: MapCardCtx, cell: number): Action | null {
  const hauler = ctx.fleets().find((f) => f.cls === ShipClass.SHIP_CLASS_DREDGER);
  return hauler
    ? { act: 'gather', id: hauler.id, n: cell, face: 'dark', label: S.o_send_hauler, key: cap('gather') }
    : null;
}

/**
 * The card of a site. `sonar` is the listening reach the hover found for
 * another hold, 0 when no honest range is known (R112).
 */
export function siteCard(ctx: MapCardCtx, site: Site | undefined, size: CardSize, sonar = 0): CardSpec {
  if (!site) return { size, title: S.nothing_there, ink: 'neutral', close: true };
  if (site.kind === SiteKind.SITE_KIND_DEPOSIT) return depositCard(ctx, site, size);
  if (site.kind === SiteKind.SITE_KIND_CONTROL_POINT) return objectCard(ctx, site, size);
  if (site.kind === SiteKind.SITE_KIND_PLAYER_BASE && site.ownerPlayer === ctx.s.playerId) {
    return ownHoldCard(ctx, site, size);
  }
  return otherSiteCard(ctx, site, size, sonar);
}

// --- the probe scan report (084 card 08) ----------------------------------

/**
 * A scout's ping as a list card (R203, R205): one row per contact with
 * the owner's faction colour, the class, the commander and the hull, and
 * SHOW ON MAP as the primary. A press on a row centres the map on that
 * contact.
 */
export function scanReportCard(ctx: MapCardCtx, l: ChatMsg): CardSpec {
  const s = ctx.s;
  const fleets = l.report?.fleets ?? [];
  const cell = l.report?.cell ?? 0;
  const rows = fleets.map((f) => {
    const spec = s.ship(f.cls);
    const ink = cssColor(f.faction);
    const at = cellAt(mpxToPx(f.xMpx), mpxToPx(f.yMpx));
    const sub = (f.ownerName ? esc(f.ownerName) + ' · ' : '') + esc(factionName(f.faction))
      + (f.weapType || f.shieldType ? ' · ' + esc(typeText(s, f.weapType, f.shieldType)) : '');
    return `<button class="ck-row" data-act="focuscell" data-n="${at}">${markSvg('contact', ink)}`
      + `<span class="ck-rn"><b style="color:${ink}">${esc(spec?.name ?? S.unknown)}</b><i>${sub}</i></span>`
      + `<span class="ck-rf">${f.hullPct > 0 ? pctFig(f.hullPct) : S.unknown}<small> ${S.report_hull}</small></span>`
      + '</button>';
  }).join('');
  // A commander mark per row would nest a button in a button, so the
  // ⚑ REPORT of each named commander stands in one line under the list.
  const marks = fleets.filter((f) => f.ownerName && f.ownerId)
    .map((f) => esc(f.ownerName) + ctx.commanderMark(f.ownerId, f.ownerName)).filter((m) => m.includes('<'));
  return {
    size: 'card', mark: markSvg('sonar', 'var(--hero-ink)'), title: S.report_title, ink: 'catalogue',
    kind: S.report_kind({ from: l.fromName, time: isoTime(Number(l.unix), { seconds: true }) }),
    status: { text: String(fleets.length), tone: 'solid' }, close: true,
    body: fleets.length > 0 ? `<div class="ck-list">${rows}</div>` : '',
    note: fleets.length === 0 ? { lines: [esc(S.report_clear)] }
      : marks.length > 0 ? { lines: [marks.join(' · ')] } : undefined,
    actions: cell ? [{ act: 'focuscell', n: cell, face: 'dark', label: S.report_show }] : undefined,
  };
}

// --- the points of interest (round 088 card 11, R2626) --------------------

/** The words and the mark of each kind of point, the seeded cache of
 *  phase 5 included (R2735). */
const POI_WORDS: Partial<Record<PoiKind, { mark: MarkKind; title: string; kind: string; note: string }>> = {
  [PoiKind.POI_KIND_CACHE]: { mark: 'cache', title: S.poi_cache, kind: S.poi_cache_kind, note: S.poi_cache_note },
  [PoiKind.POI_KIND_VENT]: { mark: 'vent', title: S.poi_vent, kind: S.poi_vent_kind, note: S.poi_vent_note },
  [PoiKind.POI_KIND_RUIN]: { mark: 'ruin', title: S.poi_ruin, kind: S.poi_ruin_kind, note: S.poi_ruin_note },
  [PoiKind.POI_KIND_GATE]: { mark: 'gate', title: S.poi_gate, kind: S.poi_gate_kind, note: S.poi_gate_note },
  [PoiKind.POI_KIND_LAIR]: { mark: 'lair', title: S.poi_lair, kind: S.poi_lair_kind, note: S.poi_lair_note },
};

/** The title of a point of interest, or '' for a kind with no words. The
 *  first-find notices keep to the kinds of phase 3 (`poiShown`). */
export function poiTitle(kind: PoiKind): string {
  return POI_WORDS[kind]?.title ?? '';
}

/**
 * The status of a point as the client last saw it (R2735): the stock of a
 * vent, the monster of a lair, the dig of a seeded cache. A vent or a lair
 * the client never heard states nothing, and a current gate and a ruin
 * keep no state of their own.
 */
function poiStatus(s: GameState, p: PointOfInterest): string {
  const seen = s.survey.live.get(p.id);
  switch (p.kind) {
    case PoiKind.POI_KIND_VENT: {
      const full = poiRules().ventStock;
      if (!seen || full <= 0) return '';
      if (seen.row.stock > 0) return S.poi_vent_stock({ n: seen.row.stock, full });
      return S.poi_vent_dry({ left: dur(s.poiLeftSec(seen)) });
    }
    case PoiKind.POI_KIND_LAIR:
      switch (seen?.row.monster) {
        case PoiMonster.POI_MONSTER_HOME: return S.poi_monster_home;
        case PoiMonster.POI_MONSTER_OUT: return S.poi_monster_out;
        case PoiMonster.POI_MONSTER_GONE: {
          const left = s.poiLeftSec(seen!);
          return left > 0 ? S.poi_monster_back({ left: dur(left) }) : S.poi_monster_gone;
        }
        default: return '';
      }
    case PoiKind.POI_KIND_CACHE: {
      const own = s.empire?.cacheFinds.some((f) => f.cell === p.cell && f.kind === CacheKind.CACHE_KIND_SEEDED);
      return seen?.row.dug || own ? S.poi_cache_dug : S.poi_cache_waits;
    }
    default: return '';
  }
}

/**
 * The card of a point of interest the survey of the faction holds (v2 §4.2,
 * round 088 card 11, R2626). The family rule of the card: a point has no
 * owner ink and no vitals. It wears the chip FOUND, the status the client
 * last saw (R2735), the depth band of its cell while the band field
 * stands, and one note of what it is. A seeded cache has a card while the
 * world latched `treasure`, as its mark does (R2714); a kind with no words
 * answers null.
 */
export function poiCard(s: GameState, p: PointOfInterest, size: CardSize = 'card'): CardSpec | null {
  const w = POI_WORDS[p.kind];
  if (!w || !poiHasCard(s, p)) return null;
  const facts: Fact[] = [];
  const status = poiStatus(s, p);
  if (status) facts.push({ key: S.f_status, val: esc(status) });
  const band = bandAt(s.depthBands, p.cell);
  if (band > 0) facts.push({ key: S.f_depth, val: esc(bandWord(band, true)) });
  // The monster kind and the design of a lair (R2719): the wire sets both
  // only while `pirates` is latched.
  const kind = p.kind === PoiKind.POI_KIND_LAIR ? monsterKindOf(p.monster) : -1;
  if (kind >= 0) {
    facts.push({ key: S.f_monster, val: esc(MONSTER_CLASS_WORDS[kind]) });
    const design = monsterDesignWord(kind, p.design);
    if (design) facts.push({ key: S.f_design, val: esc(design) });
  }
  // The credit bounty of a lair of an arcade match (arcade.md §20.4,
  // R3216): the seat of the last hit on the beast takes it.
  if (p.bounty > 0) facts.push({ key: S.f_bounty, val: esc(S.bounty_credits({ credits: p.bounty })) });
  return {
    size, mark: markSvg(w.mark, 'var(--text)'), title: w.title, ink: 'neutral', kind: w.kind,
    status: { text: S.poi_found, tone: 'on' }, close: size === 'card',
    facts, note: { lines: [esc(w.note)] },
  };
}

/** Does a point of interest have a card (R3138)? A kind with no words
 *  has none, and a seeded cache has one only while `treasure` holds. A
 *  point with no card takes no selection. */
export function poiHasCard(s: Pick<GameState, 'hasFeatureFlag'>, p: Pick<PointOfInterest, 'kind'>): boolean {
  if (!POI_WORDS[p.kind]) return false;
  return p.kind !== PoiKind.POI_KIND_CACHE || s.hasFeatureFlag('treasure');
}

/**
 * The card of a selected point of interest in the context slot (R3138),
 * or null when the survey holds no such point or the point has no card.
 * It is the card of `poiCard` with the order presses: SEND HAULER on a
 * vent, as on a deposit (`sendHauler`, R3147), and the order press of
 * phase 5, SEARCH, which opens the SEARCH picker of the cell
 * (`pointSearch`, R2699).
 */
export function pointCard(ctx: MapCardCtx, id: number): CardSpec | null {
  const s = ctx.s;
  const p = s.survey.poi(id);
  const spec = p ? poiCard(s, p, 'card') : null;
  if (!p || !spec) return null;
  const actions: Action[] = [];
  const send = p.kind === PoiKind.POI_KIND_VENT ? sendHauler(ctx, p.cell) : null;
  if (send) actions.push(send);
  const search = pointSearch(s, p.cell);
  if (search) actions.push(send ? { ...search, face: 'quiet' } : search);
  return actions.length > 0 ? { ...spec, actions } : spec;
}

/**
 * The card of a wreck (card 08, round 088 card 11, R2637). A wreck is no
 * door, so the card stands as the water peek of its cell alone: the class
 * of the sunk hull, its side and its age on the world clock. A wreck of
 * the NPC side wears the NPC ink and names no faction; a wreck of a
 * pirate names the chart fragment of its den. The wire carries no owner of
 * a wreck, so no wreck names a commander.
 */
export function wreckCard(s: GameState, w: Wreck, size: CardSize = 'peek'): CardSpec {
  const npc = w.npc !== NpcKind.NPC_KIND_NONE;
  const ago = Math.max(0, s.seasonElapsedSec() - w.sunkSec);
  const facts: Fact[] = [
    npc
      ? { key: S.f_side, val: esc(S.npc_side) }
      : { key: S.f_faction, val: esc(factionName(w.faction)) },
    { key: S.f_sunk, val: esc(S.wreck_ago({ ago: dur(ago) })) },
  ];
  // The take of a SEARCH and the age of the cargo sighting (v2 §9.6,
  // R2843): a wreck with no sighting states no cargo.
  // A sighted wreck wears the cargo bale of its map mark (R2847); a
  // recovered row states "recovered" and wears the plain mark (R2850).
  const cargo = wreckCargoFact(s, w);
  if (cargo) facts.push(cargo);
  return {
    size, mark: markSvg(wreckTakes(s, w) ? 'wreckcargo' : 'wreck', npc ? 'var(--npc)' : cssColor(w.faction)), title: S.wreck_title,
    ink: npc ? { npc: true } : { faction: w.faction }, kind: classWord(s, w.cls),
    close: size === 'card', facts,
    note: { lines: [esc(w.npc === NpcKind.NPC_KIND_PIRATE ? S.wreck_pirate_note : S.wreck_note)] },
  };
}
